import _ from 'lodash';
import Cookies from 'js-cookie';
import MobileDetect from 'mobile-detect';
import store from '@/store';
import { API_RETURN_CODE, API_RETURN_CODE_USERINFO } from '@/utils/variables';
import { i18n } from '@/plugins/lang';

/**
 * 모바일 디바이스 여부를 판단한다.
 */
const isMobile = (() => {
  const md = new MobileDetect(navigator.userAgent);
  return !!md.mobile();
})();
/**
 * 모바일 디바이스 OS를 판단한다.
 */
const isMobileOS = (() => {
  const md = new MobileDetect(navigator.userAgent);
  return md.os();
})();

const findAncestor = (el, selector) => {
  while (!el.matches(selector) && (el = el.parentElement));
  return el;
};
/**
 * 네스트 쿠키가 존재하는지 확인한다.
 */
const nest = () => Cookies.get('_nest');
/**
 * ENC 쿠키가 존재하는지 확인한다.
 */
const ENC = () => Cookies.get('ENC');
/**
 * NPP 쿠키가 존재하는지 확인한다.
 */
const NPP = () => Cookies.get('NPP');

const isMobileLogin = () => isMobile && (!!nest() || !!NPP());
/**
 * 로그인 상태여부를 반환 한다.
 */
const isLogin = () => {
  return window.inface.auth.isSignedIn();
};
/**
 * 로그아웃을 처리를 한다.
 */
const logout = () => {
  // pc 로그아웃
  // i18n.locale
  let baseUrl = `${process.env.VUE_APP_HOST}/${i18n.locale ?? i18n.locale}`;

  if (process.env.NODE_ENV !== 'production' && checkUserRegion() !== 'krpc') {
    // test queryString for test arena_tw
    // baseUrl = window.location.href;
    baseUrl = `${process.env.VUE_APP_HOST}/${i18n.locale ?? i18n.locale}`;
  }

  if (typeof window.inface !== 'undefined') {
    window.inface.auth.gotoSignOut({ redirect_uri: `${baseUrl}` });
  }
};

// 계정 정보 판단 (platform type)
const checkUserRegion = () => {
  const { auth } = window.inface;

  return auth._platform.type;
};

const checkArenaRegion = () => {
  const { auth } = window.inface;
  
  return auth._platform.region;
};

/**
 * iframe 여부에 따라 모달의 위치를 결정 한다.
 */
const selectOpenModal = (val) => {
  if (store.getters.isExchangeIframe) {
    window.parent.postMessage({ event: 'iframeModal', data: val }, '*');
  } else {
    store.dispatch('modal/openModal', val);
  }
};

/**
 * 로그인 페이지로 이동시킨다.
 */
const goToLoginPage = (url) => {
  const redirectUrl = window.location.href;

  const baseUrl = process.env.VUE_APP_HOST;

  const path = window.location.pathname;
  const lang = path.split('/')[1];

  if (typeof window.inface !== 'undefined') {
    window.inface.auth.gotoSignIn(url ? { redirect_uri: `${baseUrl}/${lang}${url}` || redirectUrl } : {});
  }
};

/**
 * 로그인 유도 레이어를 띄운다.
 */
const loginModal = (path) => {
  store.dispatch('modal/openModal', {
    type: 'confirm',
    message: i18n.t('error.common.needLogin'),
    callback: () => {
      goToLoginPage(path);
    },
  });
};

/**
 * 로그인 여부와 레이어 위치를 정한다. (API)
 */
const checkLoginIframe = (val) => {
  if (val === API_RETURN_CODE.NOT_LOGIN) {
    if (store.getters.isExchangeIframe) {
      window.parent.postMessage({ event: 'iframeLogin' }, '*');
    } else {
      goToLoginPage();
    }
  }
};
/**
 * 로그인 유도 레이어를 띄운다. (API)
 */
const checkLogin = (val) => {
  if (val === API_RETURN_CODE.NOT_LOGIN) {
    goToLoginPage();
  }
  return false;
};

/**
 * ERROR 레이어를 띄운다.
 */
const checkMaintainnance = (val) => {
  if (val === API_RETURN_CODE.MAINTAINNANCE) {
    selectOpenModal({
      type: 'alert',
      message: '서비스 점검중입니다. 이용에 불편을 드려 죄송합니다.',
    });
    return false;
  }
  if (val === API_RETURN_CODE.UNKNOWN_ERROR) {
    selectOpenModal({
      type: 'alert',
      message: i18n.t('error.common.temporaryError'),
    });
    return false;
  }
  // 메인이 아닌 곳에서 해당 언어 정보가 없을 시 메인으로 리다이렉트
  if (window.location.pathname !== `/${i18n.locale}` && val === API_RETURN_CODE.NON_LANGUAGE_DATA) {
    window.location = `${process.env.VUE_APP_HOST}/${i18n.locale}`;
  }
  return false;
};

/**
 * 유저정보 레이어를 띄운다.
 */
const checkUserInfo = (val) => {
  if (val === API_RETURN_CODE_USERINFO.NO_USERINFO) {
    selectOpenModal({
      type: 'alert',
      message: '등록된 회원정보가 없습니다.',
    });
    return true;
  }
  if (val === API_RETURN_CODE_USERINFO.NO_BUSINESSINFO) {
    selectOpenModal({
      type: 'alert',
      message: '등록된 사업자정보가 없습니다.',
    });
    return true;
  }
  if (val === API_RETURN_CODE_USERINFO.IN_PROCESS_BUSINESSINFO) {
    selectOpenModal({
      type: 'alert',
      message: '아직 처리되지 않은 사업자 신청 내역이 있습니다.',
    });
    return true;
  }
  if (val === API_RETURN_CODE_USERINFO.NO_COMPLETE_INFO) {
    selectOpenModal({
      type: 'alert',
      message: '처리 완료된 내역이 없습니다.',
    });
    return true;
  }
  return false;
};

// 특수문자 체크 및 교체
const replaceSpecialChr = (val) => {
  const regExp = /[\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@\#$%&\\\=\(\'\"]/g;
  return val.replace(regExp, '');
};

// 주소/법인명 특수문자 체크 및 교체
const replaceAddressChr = (val) => {
  const regExp = /[\/<>]/g;
  return val.replace(regExp, '');
};

/**
 * DOM에 전달받은 클래스를 추가한다.
 * @param {Element} dom - DOM 객체
 * @param {String} className - 클래스 이름
 */
const addClass = (dom, className) => {
  if (dom && className.length) {
    dom.classList.add(className);
  }
};
/**
 * DOM에 전달 받은 클래스를 제거한다.
 * @param {Element} dom - DOM 객체
 * @param {String} className - 클래스 이름
 */
const removeClass = (dom, className) => {
  if (NodeList.prototype.isPrototypeOf(dom)) {
    dom.forEach((el) => {
      el.classList.remove(className);
    });
  } else {
    dom.classList.remove(className);
  }
};
/**
 * DOM에 해당 클래스의 존재여부를 반환한다.
 * @param {Element} dom - DOM 객체
 * @param {String} className - 클래스 이름
 */
const hasClass = (dom, className) => (dom ? dom.classList.contains(className) : false);

/**
 * 앱을 시작하기 전, 모바일 여부를 판단하고 모바일일 경우 추가 처리를 한다.
 * @param {Fucntion} callback - 콜백함수
 */
const checkMobileBeforeStart = (callback) => {
  Cookies.set('isMobile', isMobile, { domain: '.nexon.com' });
  window.onload = function () {
    if (isMobile) {
      window.nest.init();
    }
    callback();
  };
};
/**
 * 타임존에 따른 시차를 계산한다.
 */
const timezoneOffset = new Date().getTimezoneOffset() * 60000;

/**
 * 문자열을 타임존에 맞게 파싱한다.
 * @param {String} date - 날짜 문자열
 */
const parseDate = (date) => {
  return Date.parse(`${date.split('+')[0]}Z`) + timezoneOffset;
};
/**
 * Date를 YYYY MM DD 형태로 변환 한다.
 * @param {Date} date - date 객체
 * @param {String} separator - 구분자 (기본값: "-")
 */
const dateToYYYYMMDD = (date, separator = '-') => {
  const MM = date.getMonth() + 1,
    DD = date.getDate();
  return [date.getFullYear(), separator, (MM > 9 ? '' : '0') + MM, separator, (DD > 9 ? '' : '0') + DD].join('');
};
/**
 * Date를 YYYY MM DD HH MM 형태로 변환한다.
 * @param {Date} date - date 객체
 */
const dateToYYYYMMDDhhmm = (date) => {
  const hh = date.getHours(),
    mm = date.getMinutes();

  return [dateToYYYYMMDD(date), ' ', (hh > 9 && hh > 0 ? '' : '0') + hh, ':', (mm > 9 ? '' : '0') + mm].join('');
};
/**
 * 서비스 정보를 저장한다.
 */
const setServiceInfo = (val) => {
  const url = val.serviceUrl;
  const serviceData = _.find(store.state.pointThumbnailList, { pathKey: url });
  if (serviceData) {
    store.dispatch('setServiceInfo', serviceData);
    return url;
  } else {
    store.dispatch('setServiceInfo', null);
    return null;
  }
};

const setServiceUrl = (serviceUrl) => {
  if (serviceUrl) {
    store.dispatch('setServiceUrl', serviceUrl);
  }
};

const preventXSS = (str) => str.replace(/\</g, '&lt;').replace(/\>/g, '&gt;');
const removeSapceFB = (str) => str.replace(/(^\s*)|(\s*$)/gi, '');

/**
 * 외부에서 유저정보 입력 페이지로 접근해 완료한 경우 처리
 */
const openerPostMessageClose = () => {
  if (window.name == 'rpRegistration') {
    window.opener.postMessage('rpRegistrationComplete', '*');
    window.close();
  }
};

const calculateImgPixel = (file) => {
  return new Promise((resolve, reject) => {
    const url = window.URL || window.webkitURL;
    const img = new Image();
    img.src = url.createObjectURL(file);
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
  });
};

export {
  isMobile,
  isMobileOS,
  findAncestor,
  isLogin,
  logout,
  checkUserRegion,
  checkArenaRegion,
  selectOpenModal,
  loginModal,
  checkLoginIframe,
  checkLogin,
  checkMaintainnance,
  checkUserInfo,
  addClass,
  removeClass,
  hasClass,
  checkMobileBeforeStart,
  goToLoginPage,
  dateToYYYYMMDD,
  dateToYYYYMMDDhhmm,
  setServiceInfo,
  setServiceUrl,
  preventXSS,
  removeSapceFB,
  parseDate,
  openerPostMessageClose,
  replaceSpecialChr,
  replaceAddressChr,
  calculateImgPixel,
};
